import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowRight, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Inline card shown below the AIAssistant when it detects "Kada X, onda Y".
 * User confirms both rows (upstream + downstream). Each row can be either a
 * pre-existing task or a fresh one. Everything is editable before save.
 *
 * Copy rules (product):
 *  - Header top: "Šta treba prvo da se završi?"
 *  - Header bottom: "Šta sledi posle?"
 *  - Save button: "Sačuvaj redosled"
 *  - No "veza / dependency / parent / upstream" jargon.
 */
export default function LinkConfirmCard({ parsed, collaborators = [], onDone, onDiscard }) {
  const [upstream, setUpstream] = useState(() => ({
    mode: parsed?.upstream?.match_type === "existing" ? "existing" : "new",
    task_id: parsed?.upstream?.task_id || null,
    title: parsed?.upstream?.title || "",
    collaborator_id: parsed?.upstream?.collaborator_id || "",
    direction: "to_me",
    priority: "srednji",
    due_date: parsed?.upstream?.due_date || "",
  }));
  const [downstream, setDownstream] = useState(() => ({
    mode: "new",
    task_id: null,
    title: parsed?.downstream?.title || "",
    collaborator_id: parsed?.downstream?.collaborator_id || "",
    direction: parsed?.downstream?.direction || "to_them",
    priority: parsed?.downstream?.priority || "srednji",
    due_date: parsed?.downstream?.due_date || "",
  }));
  const [note, setNote] = useState(parsed?.unlock_note || "");
  const [saving, setSaving] = useState(false);

  const buildSide = (s) => {
    if (s.mode === "existing") return { use_existing_id: s.task_id };
    return {
      create: {
        title: s.title.trim(),
        collaborator_id: s.collaborator_id,
        direction: s.direction,
        priority: s.priority,
        due_date: s.due_date || null,
      },
    };
  };

  const save = async () => {
    if (upstream.mode === "existing" && !upstream.task_id) return toast.error("Izaberi prethodni zadatak");
    if (upstream.mode === "new" && (!upstream.title.trim() || !upstream.collaborator_id))
      return toast.error("Popuni naslov i saradnika za prethodni zadatak");
    if (downstream.mode === "existing" && !downstream.task_id) return toast.error("Izaberi zadatak koji sledi");
    if (downstream.mode === "new" && (!downstream.title.trim() || !downstream.collaborator_id))
      return toast.error("Popuni naslov i saradnika za zadatak koji sledi");
    setSaving(true);
    try {
      const { data } = await api.post("/tasks/link/save", {
        upstream: buildSide(upstream),
        downstream: buildSide(downstream),
        unlock_note: note.trim() || null,
      });
      if (data.already_met) {
        toast.success("Redosled sačuvan · sledeći zadatak je već spreman");
      } else {
        toast.success("Redosled sačuvan · sledeći zadatak čeka");
      }
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  return (
    <div
      data-testid="link-confirm-card"
      className="surface-card p-4 border border-forest/30 bg-forest/[0.03] space-y-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-forest">
          <Sparkles className="w-4 h-4" /> AI je prepoznao redosled
        </div>
        <button
          type="button"
          onClick={onDiscard}
          data-testid="link-card-discard"
          className="text-xs text-ink-muted hover:text-terracotta px-1"
          aria-label="Odustani"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <LinkRow
        testid="link-upstream"
        title="Šta treba prvo da se završi?"
        side={upstream}
        setSide={setUpstream}
        collaborators={collaborators}
      />

      <div className="flex items-center gap-2 text-xs text-ink-muted pl-2">
        <ArrowRight className="w-4 h-4" /> a onda
      </div>

      <LinkRow
        testid="link-downstream"
        title="Šta sledi posle?"
        side={downstream}
        setSide={setDownstream}
        collaborators={collaborators}
      />

      <div>
        <Label className="text-xs text-ink-muted">Objašnjenje (opciono)</Label>
        <Input
          data-testid="link-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Kada Marija završi odobrenje, ja šaljem brief Miji"
        />
      </div>

      <div className="flex gap-2 justify-end">
        <button className="btn-ghost" onClick={onDiscard} data-testid="link-cancel-btn">
          Odustani
        </button>
        <button
          className="btn-primary"
          onClick={save}
          disabled={saving}
          data-testid="link-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {saving ? "Čuvam..." : "Sačuvaj redosled"}
        </button>
      </div>
    </div>
  );
}

function LinkRow({ testid, title, side, setSide, collaborators }) {
  const [taskResults, setTaskResults] = useState([]);
  const [taskQuery, setTaskQuery] = useState("");

  const searchTasks = async (q) => {
    setTaskQuery(q);
    try {
      const { data } = await api.get("/tasks");
      const filtered = (data || [])
        .filter((t) => t.status !== "done")
        .filter((t) => (q ? t.title.toLowerCase().includes(q.toLowerCase()) : true))
        .slice(0, 8);
      setTaskResults(filtered);
    } catch {
      setTaskResults([]);
    }
  };

  return (
    <div data-testid={testid} className="space-y-2 border-l-2 border-forest/40 pl-3">
      <div className="text-sm font-medium text-ink">{title}</div>

      <div className="flex gap-3 text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer" data-testid={`${testid}-mode-existing`}>
          <input
            type="radio"
            checked={side.mode === "existing"}
            onChange={() => { setSide({ ...side, mode: "existing" }); searchTasks(""); }}
          />
          <span>Postojeći zadatak</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer" data-testid={`${testid}-mode-new`}>
          <input
            type="radio"
            checked={side.mode === "new"}
            onChange={() => setSide({ ...side, mode: "new" })}
          />
          <span>Napravi novi</span>
        </label>
      </div>

      {side.mode === "existing" ? (
        <div>
          <Input
            data-testid={`${testid}-search`}
            placeholder="Traži postojeći zadatak..."
            value={taskQuery}
            onChange={(e) => searchTasks(e.target.value)}
          />
          <div className="max-h-40 overflow-y-auto mt-1 border border-line rounded-md divide-y divide-line bg-white">
            {taskResults.length === 0 && (
              <div className="text-xs text-ink-muted p-2">
                {taskQuery ? "Nema rezultata" : "Kucaj da tražiš..."}
              </div>
            )}
            {taskResults.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`${testid}-pick-${t.id}`}
                onClick={() => setSide({ ...side, task_id: t.id, title: t.title })}
                className={`w-full text-left text-sm px-3 py-1.5 hover:bg-surface-secondary ${
                  side.task_id === t.id ? "bg-forest/10" : ""
                }`}
              >
                {t.title}
              </button>
            ))}
          </div>
          {side.task_id && side.title && (
            <div className="text-xs text-forest mt-1 truncate">Izabrano: {side.title}</div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Input
            data-testid={`${testid}-title`}
            value={side.title}
            onChange={(e) => setSide({ ...side, title: e.target.value })}
            placeholder="Naslov zadatka"
          />
          <div className="grid grid-cols-2 gap-2">
            <Select value={side.collaborator_id} onValueChange={(v) => setSide({ ...side, collaborator_id: v })}>
              <SelectTrigger data-testid={`${testid}-collab`} className="bg-white">
                <SelectValue placeholder="Saradnik" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-60">
                {collaborators.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={side.direction} onValueChange={(v) => setSide({ ...side, direction: v })}>
              <SelectTrigger data-testid={`${testid}-direction`} className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="to_me">On duguje meni</SelectItem>
                <SelectItem value="to_them">Ja dugujem</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={side.priority} onValueChange={(v) => setSide({ ...side, priority: v })}>
              <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="nizak">Nizak</SelectItem>
                <SelectItem value="srednji">Srednji</SelectItem>
                <SelectItem value="visok">Visok</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              data-testid={`${testid}-due`}
              value={side.due_date || ""}
              onChange={(e) => setSide({ ...side, due_date: e.target.value })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
